import React from "react";

import useEmblaCarousel from "embla-carousel-react";
import Lottie from "lottie-react";

import poofAnimation from "../assets/poof2.json";

const DeletableCarousel = ({
  items = [],
  onItemClick,
  onLongPressStart,
  onLongPressEnd,
  deletingItems = {},
  itemsVisible = 7.8,
  type = "poster", // "poster" for movies/series, "logo" for TV
  seeAllCount = 0,
  onSeeAll,
  moveModeId = null,
  onMoveLeft,
  onMoveRight,
  onMoveDone,
}) => {
  const [emblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: false,
    dragFree: true,
  });

  if (!items || items.length === 0) return null;

  const slideStyle = {
    flex: `0 0 calc(100% / ${itemsVisible})`,
    minWidth: 0,
  };

  // Overlay shown on the item that's in "move mode" (← reposition → / ✓ done).
  const renderMoveControls = () => (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center gap-2 rounded-xl bg-black/70"
      role="presentation"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        aria-label="Move left"
        className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onMoveLeft?.();
        }}
      >
        <i className="fas fa-arrow-left" />
      </button>
      <button
        aria-label="Done"
        className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white transition-colors hover:bg-blue-700"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onMoveDone?.();
        }}
      >
        <i className="fas fa-check" />
      </button>
      <button
        aria-label="Move right"
        className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onMoveRight?.();
        }}
      >
        <i className="fas fa-arrow-right" />
      </button>
    </div>
  );

  const renderItem = (item, idx) => {
    const isDeleting = deletingItems[item.id];
    const isMoving = moveModeId != null && item.id === moveModeId;
    const key = item.id || idx;

    const press = {
      onClick: () => {
        if (isMoving || isDeleting) return;
        onItemClick?.(item);
      },
      onMouseDown: () => !isMoving && onLongPressStart(item),
      onMouseLeave: () => onLongPressEnd(item.id),
      onMouseUp: () => onLongPressEnd(item.id),
      onTouchEnd: () => onLongPressEnd(item.id),
      onTouchStart: () => !isMoving && onLongPressStart(item),
    };
    const movingRing = isMoving
      ? "ring-4 ring-blue-500 ring-offset-2 ring-offset-transparent"
      : "";

    if (type === "logo") {
      const logoUrl =
        item.logoPath ||
        item.logo ||
        item.logo_path ||
        item.stream_icon ||
        item.streamIcon ||
        item.channel_logo ||
        item.channelLogo ||
        item.icon ||
        item.image ||
        item.channelIcon ||
        item.channel_icon ||
        item.logoUrl ||
        item.logo_url ||
        item.iconUrl ||
        item.icon_url;

      return (
        <div className="min-w-0" key={key} style={slideStyle}>
          <article className="transform-gpu">
            <div className="relative group">
              <div
                className={`lunova-card is-landscape relative card-radius-landscape aspect-video cursor-pointer ${movingRing}`}
                style={{
                  background:
                    "linear-gradient(135deg, rgba(30,30,40,0.9) 0%, rgba(50,50,65,0.9) 100%)",
                  transformOrigin: "center center",
                  boxShadow:
                    "0 10px 25px -8px rgba(0,0,0,0.4), " +
                    "0 6px 15px -4px rgba(0,0,0,0.3)",
                }}
                {...press}
              >
                <div className="lunova-card-clip">
                  {isDeleting ? (
                    <div className="relative h-full flex items-center justify-center aspect-video">
                      <Lottie
                        animationData={poofAnimation}
                        autoplay={true}
                        loop={false}
                        style={{ width: 200, height: 200 }}
                      />
                    </div>
                  ) : (
                    <div className="relative h-full flex flex-col">
                      <div className="flex-1 flex items-center justify-center p-5 rounded-lg overflow-hidden">
                        {logoUrl ? (
                          <img
                            alt={item.name || item.title || "Content"}
                            className="max-h-20 max-w-full object-contain"
                            draggable={false}
                            src={logoUrl}
                            onDragStart={(e) => e.preventDefault()}
                          />
                        ) : (
                          <i className="fa-solid fa-image text-white/50 text-3xl" />
                        )}
                      </div>

                      <div className="absolute bottom-3 left-0 right-0 flex justify-center">
                        <div
                          className="h-2 bg-gray-600 rounded-full overflow-hidden"
                          style={{ width: "70%" }}
                        >
                          <div
                            className="h-full rounded-full transition-all duration-300"
                            style={{
                              backgroundColor: "#6495ED",
                              width: `${Math.min(
                                Math.max(0, item.progress || 0),
                                100
                              )}%`,
                            }}
                            title={`Progress: ${Math.round(item.progress || 0)}%`}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {isMoving && renderMoveControls()}
                </div>
              </div>

              {!isDeleting && (
                <div className="text-white text-sm font-bold truncate text-center mt-2 px-1 select-none">
                  {item.currentProgram ||
                    item.epgTitle ||
                    item.nowPlaying ||
                    item.name ||
                    item.channel_name ||
                    item.title ||
                    "Channel"}
                </div>
              )}
            </div>
          </article>
        </div>
      );
    }

    // Poster style for movies/series
    const posterUrl = item.poster || item.posterPath || item.thumbnail;

    return (
      <div className="min-w-0" key={key} style={slideStyle}>
        <article className="group cursor-pointer">
          <div
            className={`lunova-card relative card-radius-poster ${movingRing}`}
            style={{ filter: "drop-shadow(0 20px 25px rgba(0, 0, 0, 0.15))" }}
            {...press}
          >
            <div className="lunova-card-clip">
              {isDeleting ? (
                <div className="w-full aspect-[2/3] flex items-center justify-center">
                  <Lottie
                    animationData={poofAnimation}
                    autoplay={true}
                    loop={false}
                    style={{ width: 200, height: 200 }}
                  />
                </div>
              ) : (
                <>
                  {posterUrl ? (
                    <img
                      alt={item.title || item.name}
                      className="w-full h-auto object-cover aspect-[2/3]"
                      draggable={false}
                      src={posterUrl}
                      onDragStart={(e) => e.preventDefault()}
                    />
                  ) : (
                    <div className="w-full aspect-[2/3] bg-gray-800 flex items-center justify-center">
                      <i className="fas fa-film text-gray-600 text-3xl" />
                    </div>
                  )}
                </>
              )}

              {isMoving && renderMoveControls()}
            </div>
          </div>

          {!isDeleting &&
            (item.rottenTomatoes?.audience?.score ||
              item.rottenTomatoes?.critics?.score ||
              item.rating) && (
              <div className="flex justify-center items-center gap-3 mt-2 text-xs">
                {item.rottenTomatoes?.critics?.score && (
                  <div className="flex items-center gap-1">
                    <span className="text-lg">🍅</span>
                    <span className="text-white font-semibold">
                      {item.rottenTomatoes.critics.score}%
                    </span>
                  </div>
                )}
                {item.rottenTomatoes?.audience?.score && (
                  <div className="flex items-center gap-1">
                    <span className="text-lg">🍿</span>
                    <span className="text-white font-semibold">
                      {item.rottenTomatoes.audience.score}%
                    </span>
                  </div>
                )}
                {!item.rottenTomatoes?.audience?.score &&
                  !item.rottenTomatoes?.critics?.score &&
                  item.rating && (
                    <div className="flex items-center gap-1">
                      <span className="text-yellow-500">⭐</span>
                      <span className="text-white font-semibold">
                        {item.rating}
                      </span>
                    </div>
                  )}
              </div>
            )}
        </article>
      </div>
    );
  };

  const renderSeeAll = () => (
    <div className="min-w-0" key="see-all" style={slideStyle}>
      <button
        className={`flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-white/20 bg-white/[0.03] text-gray-400 transition-colors hover:border-white/40 hover:text-white ${
          type === "logo" ? "aspect-video" : "aspect-[2/3]"
        }`}
        type="button"
        onClick={onSeeAll}
      >
        <i className="fas fa-table-cells text-3xl" />
        <span className="text-sm font-medium">See All ({seeAllCount})</span>
      </button>
    </div>
  );

  return (
    <div
      className="overflow-x-clip overflow-y-visible select-none"
      ref={emblaRef}
      style={{ padding: "10px 2px 10px 10px" }}
    >
      <div className="flex gap-4">
        {items.map((item, idx) => renderItem(item, idx))}
        {onSeeAll && seeAllCount > 0 && renderSeeAll()}
      </div>
    </div>
  );
};

export default DeletableCarousel;
